import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { useActiveCompany } from '@/lib/active-company-context';
import { useApi } from '@/lib/api-context';

import {
  requestListResponseSchema,
  requestResponseSchema,
  summaryResponseSchema,
  type ApproveInput,
  type CreateRequestInput,
  type DocumentRequest,
  type RequestListParams,
  type RequestPage,
} from './types';

// Datos de las solicitudes de documento (G3-1): bandeja, contador de pendientes, detalle
// y las acciones (crear, aprobar, rechazar, cancelar). Toda consulta lleva la empresa
// activa en la clave; cada acción refresca la bandeja, la Lista Maestra y la ficha.

const BASE = '/api/v1/quality/document-requests';
export const REQUEST_PAGE_SIZE = 20;

export function useRequestList(params: RequestListParams): UseQueryResult<RequestPage> {
  const api = useApi();
  const { activeCompanyId } = useActiveCompany();
  return useQuery({
    queryKey: ['document-requests', activeCompanyId, 'list', params],
    queryFn: async () => {
      const q = new URLSearchParams();
      if (params.status !== null) {
        q.set('status', params.status);
      }
      if (params.mine) {
        q.set('mine', 'true');
      }
      q.set('page', String(params.page));
      q.set('limit', String(REQUEST_PAGE_SIZE));
      const res = requestListResponseSchema.parse(
        await api.get<unknown>(`${BASE}?${q.toString()}`),
      );
      return { rows: res.data, total: res.meta.total };
    },
    enabled: activeCompanyId !== null,
    placeholderData: (prev) => prev,
  });
}

/** Solicitudes pendientes que el lector ve (Calidad: todas; resto: las suyas). */
export function useRequestSummary(enabled: boolean): UseQueryResult<number> {
  const api = useApi();
  const { activeCompanyId } = useActiveCompany();
  return useQuery({
    queryKey: ['document-requests', activeCompanyId, 'summary'],
    queryFn: async () =>
      summaryResponseSchema.parse(await api.get<unknown>(`${BASE}/summary`)).data.pending,
    enabled: enabled && activeCompanyId !== null,
  });
}

export function useDocumentRequest(id: string | null): UseQueryResult<DocumentRequest> {
  const api = useApi();
  const { activeCompanyId } = useActiveCompany();
  return useQuery({
    queryKey: ['document-requests', activeCompanyId, 'detail', id],
    queryFn: async () =>
      requestResponseSchema.parse(await api.get<unknown>(`${BASE}/${id ?? ''}`)).data,
    enabled: id !== null && activeCompanyId !== null,
  });
}

function useRefresh(): () => Promise<void> {
  const qc = useQueryClient();
  return async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['document-requests'] }),
      qc.invalidateQueries({ queryKey: ['master-list'] }),
      qc.invalidateQueries({ queryKey: ['dossier'] }),
    ]);
  };
}

function useRequestAction<V>(
  path: (input: V) => { url: string; body: unknown },
): UseMutationResult<DocumentRequest, Error, V> {
  const api = useApi();
  const refresh = useRefresh();
  return useMutation({
    mutationFn: async (input: V) => {
      const { url, body } = path(input);
      return requestResponseSchema.parse(await api.post<unknown>(url, body)).data;
    },
    onSuccess: refresh,
  });
}

export function useCreateRequest(): UseMutationResult<DocumentRequest, Error, CreateRequestInput> {
  return useRequestAction((input: CreateRequestInput) => ({ url: BASE, body: input }));
}

export function useApproveRequest(
  id: string,
): UseMutationResult<DocumentRequest, Error, ApproveInput> {
  return useRequestAction((input: ApproveInput) => ({ url: `${BASE}/${id}/approve`, body: input }));
}

export function useRejectRequest(id: string): UseMutationResult<DocumentRequest, Error, string> {
  return useRequestAction((reason: string) => ({ url: `${BASE}/${id}/reject`, body: { reason } }));
}

export function useCancelRequest(id: string): UseMutationResult<DocumentRequest, Error, void> {
  return useRequestAction(() => ({ url: `${BASE}/${id}/cancel`, body: {} }));
}
